import type { KeyboardEvent, ReactNode } from "react"
import { useCallback, useEffect, useId, useRef, useState } from "react"

import { cx } from "../lib/cx"
import styles from "./tooltip.module.css"

/**
 * Подсказка при наведении.
 *
 * ТРИ ПРАВИЛА, КОТОРЫЕ ДЕЛАЮТ ЕЁ ПРИГОДНОЙ, А НЕ КРАСИВОЙ.
 *
 * 1. Она НЕ единственный способ получить сведения (прямая подпись под макетом).
 *    Всё, что сказано в подсказке, обязано быть доступно и иначе — в тексте рядом,
 *    в подписи поля, на экране подробностей. Мышью подсказку не откроет человек
 *    с сенсорным экраном, а их у нас целый мастер на телефоне.
 *
 * 2. Задержка 400 мс — только для мыши. Курсор, проезжающий по ряду значков, не должен
 *    оставлять за собой шлейф всплывающих плашек. Клавиатурному фокусу задержка не нужна:
 *    туда попадают намеренно, и ждать полсекунды после Tab — это просто задержка.
 *
 * 3. Escape закрывает. Подсказка, накрывшая собой соседнюю кнопку, обязана убираться
 *    без движения мышью.
 */
export function Tooltip(props: {
  /** Что поясняем. Элемент обязан быть доступен с клавиатуры сам по себе. */
  readonly children: ReactNode
  readonly text: string
  /** Открывать влево — у правого края экрана. */
  readonly alignEnd?: boolean
  readonly className?: string | undefined
}): ReactNode {
  const id = useId()
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  // Таймер переживает размонтирование, если его не снять: подсказка успеет
  // «открыться» у элемента, которого уже нет.
  useEffect(() => clearTimer, [clearTimer])

  const openLater = useCallback(() => {
    clearTimer()
    timer.current = setTimeout(() => setOpen(true), 400)
  }, [clearTimer])

  const close = useCallback(() => {
    clearTimer()
    setOpen(false)
  }, [clearTimer])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === "Escape" && open) {
        // Не даём Escape уйти выше: он не должен заодно закрыть диалог,
        // внутри которого стоит подсказка.
        event.stopPropagation()
        close()
      }
    },
    [close, open],
  )

  return (
    <span
      className={cx(styles.wrap, props.className)}
      onMouseEnter={openLater}
      onMouseLeave={close}
      onFocus={() => setOpen(true)}
      onBlur={close}
      onKeyDown={onKeyDown}
    >
      {/*
       * aria-describedby ставится на обёртку, а не на сам элемент: библиотека не знает,
       * что ей передали — кнопку, значок или ссылку, и клонировать чужой элемент,
       * чтобы дописать ему атрибут, значит сломать тот, у кого этот атрибут уже есть.
       */}
      <span aria-describedby={open ? id : undefined}>{props.children}</span>
      {open ? (
        <span
          id={id}
          role="tooltip"
          className={cx(styles.bubble, props.alignEnd === true ? styles.alignEnd : undefined)}
        >
          {props.text}
        </span>
      ) : null}
    </span>
  )
}
